import { Course } from '@/types/courseTypes';

export const web3FundamentalsCourse: Course = {
  id: 'web3-fundamentals',
  title: 'Web3 Development Fundamentals',
  description: 'Learn the fundamentals of Web3 development, blockchain interaction, and decentralized applications.',
  level: 'Intermediate',
  totalDuration: '10 hours',
  image: '/api/placeholder/400/300',
  prerequisites: ['JavaScript/TypeScript', 'React basics', 'Basic blockchain concepts'],
  learningOutcomes: [
    'Understand Web3 architecture and concepts',
    'Interact with blockchain networks using Web3.js/Ethers.js',
    'Build decentralized applications (dApps)',
    'Integrate wallet connections and transactions'
  ],
  modules: [
    {
      id: 'web3-architecture-concepts',
      title: 'Web3 Architecture & Concepts',
      description: 'Understanding Web3 fundamentals and blockchain interaction',
      content: `## Web3 Architecture & Fundamental Concepts

Web3 represents the next evolution of the internet, moving from centralized to decentralized systems powered by blockchain technology. This comprehensive module provides a foundation for understanding Web3 principles and building decentralized applications.

### Understanding Web3 vs Web2

**Web2 (Current Internet)**:
- Centralized servers and databases
- Platform-owned user data
- Single points of failure
- Intermediaries control transactions
- Limited user ownership and control

**Web3 (Decentralized Internet)**:
- Distributed networks and consensus
- User-owned data and identity
- Fault-tolerant and censorship-resistant
- Peer-to-peer transactions without intermediaries
- True digital ownership through blockchain

### Core Web3 Technologies

**Blockchain Networks**: Distributed ledgers that maintain a continuously growing list of records (blocks) linked through cryptographic hashes. Popular networks include Ethereum, Polygon, Binance Smart Chain, and Solana.

**Smart Contracts**: Self-executing contracts with terms directly written into code. They automatically enforce agreement terms without intermediaries, enabling trustless interactions.

**Decentralized Storage**: Systems like IPFS (InterPlanetary File System) and Arweave provide distributed storage alternatives to centralized cloud services.

**Consensus Mechanisms**: Protocols that ensure network agreement on the blockchain state, including Proof of Work (Bitcoin), Proof of Stake (Ethereum 2.0), and other variants.

### Web3 Application Architecture

**Frontend Layer**: Traditional web technologies (React, Vue, Angular) enhanced with Web3 libraries for blockchain interaction.

**Web3 Middleware**: Libraries like Web3.js, Ethers.js, or Wagmi that bridge frontend applications with blockchain networks.

**Blockchain Layer**: Smart contracts deployed on blockchain networks that handle business logic and data storage.

**Storage Layer**: Combination of on-chain storage for critical data and decentralized storage (IPFS) for larger files.

### Getting Started Checklist

1. Understand blockchain fundamentals and cryptocurrency basics
2. Set up development environment with Node.js and Web3 tools
3. Create and configure a MetaMask wallet
4. Learn Solidity basics for smart contract development
5. Explore existing dApps to understand user flows
6. Practice with testnets before deploying to mainnet
7. Stay updated with rapidly evolving Web3 ecosystem`,
      order: 1,
      estimatedTime: '2.5 hours',
      duration: '2.5 hours',
      difficulty: 'intermediate',
      objectives: [
        'Understand Web3 vs Web2 differences',
        'Learn blockchain interaction patterns',
        'Explore decentralized storage solutions'
      ]
    },
    {
      id: 'wallet-integration',
      title: 'Wallet Integration & Authentication',
      description: 'Connecting wallets and handling user authentication',
      content: 'Implement wallet connections, user authentication, and transaction signing.',
      order: 2,
      estimatedTime: '2.5 hours',
      duration: '2.5 hours',
      difficulty: 'intermediate',
      objectives: [
        'Integrate MetaMask and other wallets',
        'Handle user authentication flows',
        'Manage wallet connections and states'
      ]
    },
    {
      id: 'smart-contract-interaction',
      title: 'Smart Contract Interaction',
      description: 'Reading from and writing to smart contracts',
      content: 'Learn to interact with smart contracts using Web3 libraries and handle contract events.',
      order: 3,
      estimatedTime: '2.5 hours',
      duration: '2.5 hours',
      difficulty: 'intermediate',
      objectives: [
        'Read contract data and states',
        'Execute contract transactions',
        'Listen to contract events'
      ]
    },
    {
      id: 'dapp-development',
      title: 'Complete dApp Development',
      description: 'Building full-featured decentralized applications',
      content: 'Build complete dApps with frontend integration, state management, and user experience.',
      order: 4,
      estimatedTime: '2.5 hours',
      duration: '2.5 hours',
      difficulty: 'advanced',
      objectives: [
        'Build complete dApp interfaces',
        'Implement state management for Web3',
        'Handle transaction states and errors'
      ]
    }
  ]
};

export const solidityAdvancedCourse: Course = {
  id: 'solidity-advanced',
  title: 'Advanced Solidity & Smart Contract Development',
  description: 'Master advanced Solidity programming and smart contract development patterns.',
  level: 'Advanced',
  totalDuration: '12 hours',
  image: '/api/placeholder/400/300',
  prerequisites: ['Solidity basics', 'Blockchain fundamentals', 'Programming experience'],
  learningOutcomes: [
    'Implement advanced Solidity patterns',
    'Build secure and gas-optimized contracts',
    'Design upgradeable contract architectures',
    'Implement DeFi protocols and patterns'
  ],
  modules: [
    {
      id: 'advanced-solidity-patterns',
      title: 'Advanced Solidity Patterns',
      description: 'Complex contract patterns and design principles',
      content: 'Learn proxy patterns, factory patterns, and advanced inheritance structures.',
      order: 1,
      estimatedTime: '3 hours',
      duration: '3 hours',
      difficulty: 'advanced',
      objectives: [
        'Implement proxy and delegate patterns',
        'Use factory and registry patterns',
        'Master inheritance and interfaces'
      ]
    },
    {
      id: 'contract-security-optimization',
      title: 'Security & Gas Optimization',
      description: 'Writing secure and efficient smart contracts',
      content: 'Learn security best practices, common vulnerabilities, and gas optimization techniques.',
      order: 2,
      estimatedTime: '3 hours',
      duration: '3 hours',
      difficulty: 'advanced',
      objectives: [
        'Identify and prevent security vulnerabilities',
        'Optimize gas usage and costs',
        'Implement security audit practices'
      ]
    },
    {
      id: 'upgradeable-contracts',
      title: 'Upgradeable Contract Architecture',
      description: 'Designing and implementing upgradeable smart contracts',
      content: 'Build upgradeable contract systems using proxy patterns and governance mechanisms.',
      order: 3,
      estimatedTime: '3 hours',
      duration: '3 hours',
      difficulty: 'advanced',
      objectives: [
        'Implement transparent proxy patterns',
        'Design governance mechanisms',
        'Handle state migrations safely'
      ]
    },
    {
      id: 'defi-protocol-development',
      title: 'DeFi Protocol Development',
      description: 'Building decentralized finance protocols and applications',
      content: 'Develop DeFi protocols including AMMs, lending platforms, and yield farming contracts.',
      order: 4,
      estimatedTime: '3 hours',
      duration: '3 hours',
      difficulty: 'advanced',
      objectives: [
        'Build automated market makers',
        'Implement lending/borrowing protocols',
        'Create yield farming mechanisms'
      ]
    }
  ]
};

export const defiDevelopmentCourse: Course = {
  id: 'defi-development',
  title: 'DeFi Protocol Development',
  description: 'Learn to build decentralized finance protocols and applications from scratch.',
  level: 'Advanced',
  totalDuration: '14 hours',
  image: '/api/placeholder/400/300',
  prerequisites: ['Solidity proficiency', 'Smart contract development', 'DeFi understanding'],
  learningOutcomes: [
    'Build complex DeFi protocols',
    'Implement liquidity mining and staking',
    'Create automated market makers',
    'Design tokenomics and governance systems'
  ],
  modules: [
    {
      id: 'defi-protocol-architecture',
      title: 'DeFi Protocol Architecture',
      description: 'Designing scalable and secure DeFi protocol architectures',
      content: 'Learn to architect complex DeFi protocols with proper security and scalability.',
      order: 1,
      estimatedTime: '3.5 hours',
      duration: '3.5 hours',
      difficulty: 'advanced',
      objectives: [
        'Design protocol architectures',
        'Implement modular contract systems',
        'Handle cross-protocol interactions'
      ]
    },
    {
      id: 'amm-development',
      title: 'Automated Market Maker Development',
      description: 'Building automated market makers and liquidity protocols',
      content: 'Develop AMM protocols with different curve implementations and liquidity management.',
      order: 2,
      estimatedTime: '3.5 hours',
      duration: '3.5 hours',
      difficulty: 'advanced',
      objectives: [
        'Implement different AMM curves',
        'Build liquidity provider mechanisms',
        'Handle slippage and price impact'
      ]
    },
    {
      id: 'lending-borrowing-protocols',
      title: 'Lending & Borrowing Protocols',
      description: 'Creating decentralized lending and borrowing platforms',
      content: 'Build lending protocols with interest rate models, collateralization, and liquidation.',
      order: 3,
      estimatedTime: '3.5 hours',
      duration: '3.5 hours',
      difficulty: 'advanced',
      objectives: [
        'Implement interest rate models',
        'Build collateralization systems',
        'Handle liquidation mechanisms'
      ]
    },
    {
      id: 'tokenomics-governance',
      title: 'Tokenomics & Governance',
      description: 'Designing token economics and governance mechanisms',
      content: 'Create sustainable tokenomics models and decentralized governance systems.',
      order: 4,
      estimatedTime: '3.5 hours',
      duration: '3.5 hours',
      difficulty: 'advanced',
      objectives: [
        'Design token distribution models',
        'Implement governance mechanisms',
        'Create incentive alignment systems'
      ]
    }
  ]
};

export const WEB3_DEVELOPMENT_COURSES = [
  web3FundamentalsCourse,
  solidityAdvancedCourse,
  defiDevelopmentCourse
];